/**
 * Centralized multi-select filter buckets for the inventory page.
 * Each bucket has an `id` (used in URL params, comma-joined) and a
 * numeric range or value used by useInventory to build the query.
 */

export const PAYMENT_FACTOR = 0.012133; // 120 mo @ 8% APR (matches hook)

export interface RangeBucket {
  id: string;
  label: string;
  min?: number;
  max?: number;
}

export const PAYMENT_BUCKETS: RangeBucket[] = [
  { id: "u199", label: "Under $199/mo", max: 199 },
  { id: "200-299", label: "$200–$299/mo", min: 200, max: 299 },
  { id: "300-399", label: "$300–$399/mo", min: 300, max: 399 },
  { id: "400-499", label: "$400–$499/mo", min: 400, max: 499 },
  { id: "500-699", label: "$500–$699/mo", min: 500, max: 699 },
  { id: "700p", label: "$700+/mo", min: 700 },
];

export const WEIGHT_BUCKETS: RangeBucket[] = [
  { id: "u3500", label: "Under 3,500 lbs", max: 3500 },
  { id: "3500-5500", label: "3,500–5,500 lbs", min: 3500, max: 5500 },
  { id: "5500-8500", label: "5,500–8,500 lbs", min: 5500, max: 8500 },
  { id: "8500-11000", label: "8,500–11,000 lbs", min: 8500, max: 11000 },
  { id: "11000-14000", label: "11,000–14,000 lbs", min: 11000, max: 14000 },
  { id: "14000p", label: "14,000+ lbs", min: 14000 },
];

export const LENGTH_BUCKETS: RangeBucket[] = [
  { id: "u20", label: "Under 20 ft", max: 20 },
  { id: "20-25", label: "20–25 ft", min: 20, max: 25 },
  { id: "25-30", label: "25–30 ft", min: 25, max: 30 },
  { id: "30-35", label: "30–35 ft", min: 30, max: 35 },
  { id: "35-40", label: "35–40 ft", min: 35, max: 40 },
  { id: "40p", label: "40+ ft", min: 40 },
];

export const SLEEPS_BUCKETS: RangeBucket[] = [
  { id: "2-4", label: "Sleeps 2–4", min: 2, max: 4 },
  { id: "5-6", label: "Sleeps 5–6", min: 5, max: 6 },
  { id: "7-8", label: "Sleeps 7–8", min: 7, max: 8 },
  { id: "9p", label: "Sleeps 9+", min: 9 },
];

export const PRICE_BUCKETS: RangeBucket[] = [
  { id: "u15k", label: "Under $15,000", max: 15000 },
  { id: "15-25k", label: "$15,000–$25,000", min: 15000, max: 25000 },
  { id: "25-40k", label: "$25,000–$40,000", min: 25000, max: 40000 },
  { id: "40-60k", label: "$40,000–$60,000", min: 40000, max: 60000 },
  { id: "60-90k", label: "$60,000–$90,000", min: 60000, max: 90000 },
  { id: "90kp", label: "$90,000+", min: 90000 },
];

/** Bed-type options. These match values stored in `floorplan_features` (and are
 *  queried with the same `overlaps` operator that powers the Features filter). */
export const BED_TYPE_OPTIONS = [
  "King Bed",
  "Murphy Bed",
  "Bunkhouse",
  "Rear Twin",
  "Front Bedroom",
  "Rear Bedroom",
];

/** Full list of floorplan features available as multi-select. */
export const FEATURE_OPTIONS = [
  "Bath and a Half", "Bunk Over Cab", "Bunkhouse", "Front Bath",
  "Front Bedroom", "Front Cargo Deck", "Front Entertainment", "Front Kitchen",
  "Front Living", "King Bed", "Kitchen Island", "Lithium Battery", "Loft",
  "Murphy Bed", "Outdoor Entertainment", "Outdoor Kitchen", "Rear Bath",
  "Rear Bedroom", "Rear Entertainment", "Rear Kitchen", "Rear Living Area",
  "Rear Twin", "Side Ramp", "Solar Panels", "Tankless Water Heater",
  "Theater Seating", "Two Entry/Exit Doors", "Two Full Baths",
  "U Shaped Dinette", "V-Nose", "Walk-Thru Bath", "Wheelchair Accessible",
];

/** URL helpers — multi-value params are stored comma-separated. */
export const parseList = (v: string | null): string[] =>
  v ? v.split(",").filter(Boolean) : [];

export const stringifyList = (arr: string[]): string =>
  arr.filter(Boolean).join(",");

/** Convert selected bucket ids to {min,max} pairs for a column. */
export function bucketsToRanges(
  ids: string[],
  defs: RangeBucket[],
): Array<{ min?: number; max?: number }> {
  return ids
    .map((id) => defs.find((d) => d.id === id))
    .filter((b): b is RangeBucket => !!b)
    .map(({ min, max }) => ({ ...(min !== undefined ? { min } : {}), ...(max !== undefined ? { max } : {}) }));
}